import React, { Component } from "react";
import { Tabs, Tab, Row, Button, Form, Modal, Col, Spinner, Card } from "react-bootstrap";
import gql from "graphql-tag";
import { withRouter } from "react-router-dom";
import compose from "lodash.flowright";
import { graphql, withApollo } from "react-apollo";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { MathRenderer, mathJaxConfig } from "./PdfQuestionsSec";
import { Container } from "react-bootstrap";
import "./_pdf.scss";
import CardLessDataTableWithOutSearch from "../datatables/CardLessDataTableWithOutSearch";
import * as Cookies from "es-cookie";
import SelectDropDown from "../selectdropdown/SelectDropDown";
import ExamAnswerExplanationUpload from "./ExamAnswerExplanationUpload";
import { canEdit } from "../../../utils/modulePermissions";

// Role permissions for this tab. PdfTabs renders inside CreateExamMadeEasy (the
// "PDF Questions" tab of Exam Creation), so it rides on the same grant.
//
// Match on module_key ONLY, never on module_name: "Online Exams" is the GROUP shared
// by Exam_Made_Easy, Manage_Exams, Quiz and View_Results, and can() allows an action
// when ANY matched row grants it — so matching the group name would let a role with
// edit on a sibling module inherit edit here.
const EXAM_MADE_EASY_MODULE = ["Exam_Made_Easy"];

/* ---------------- GRAPHQL ---------------- */

const FETCH_PDF_FILES_DATA = gql`
  query AIMLQuestionBankCollegePdf($exams: [String], $institute_id: Int!, $pexam_type: String, $page: Int, $limit: Int) {
    getAIMLFiles: getAIMLQuestionBankCollegePdf(exams: $exams, institute_id: $institute_id, pexam_type: $pexam_type, page: $page, limit: $limit) {
      id
      file_name
      exam_name
      pexam_type
      timestamp
    }
  }
`;

const FETCH_GLOBALS = gql`
   query globals($institution_id: Int!) {
    globals(institution_id: $institution_id){
        exams{
            id
            exam
        }
        classes{
            id
            class
        }
    }
}`

const instituteId = parseInt(Cookies.get("institutionid"));
const FILTERS = [
  { value: "list", label: "PDF List", icon: "fas fa-list" },
  { value: "upload", label: "Upload PDF", icon: "fas fa-cloud-upload-alt" },
  ...instituteId === 119 ? [{ value: "answer-explanation", label: "Answer & Explanation", icon: "fas fa-key" }] : []
];

class PdfTabs extends Component {

  state = {
    showModal: false,
    showCreateExamModal: false,
    questions: [],
    modalLoading: false,
    questionPdf: null,
    solutionPdf: null,
    questionError: "",
    solutionError: "",
    examError: "",
    examTypeError: "",
    page: 1,
    limit: 10,
    search: "",
    uploading: false,
    uploadMessage: "",
    uploadError: "",
    selectedExam: "",
    selectedExamType: "",
    activeTab: "list",
    modalPage: 1,
    modalLimit: 25,
    modalTotalQuestions: 0,
    activeRow: null,

    // Shown when a role without is_edit clicks an action.
    accessDeniedShow: false,
  };

  /* ---------------- ACCESS CONTROL ---------------- */

  // Every button and icon here stays VISIBLE and enabled for a view-only role —
  // nothing is hidden, disabled or redirected away. The click is allowed through, the
  // grant is checked, and a missing grant opens the Access Denied modal instead of
  // performing the action. Reads (the eye / "View Questions", the search, the exam
  // filters, paging) are not gated; only the writes are.
  //
  // Admins (userlevel 1/2) and roles with no configured grants keep full access —
  // see can() in utils/modulePermissions.
  showAccessDenied = () => this.setState({ accessDeniedShow: true });
  hideAccessDenied = () => this.setState({ accessDeniedShow: false });

  // Returns false and pops the modal when the grant is missing, so callers read as:
  // `if (!this.requireEdit()) return;`
  requireEdit = () => {
    if (canEdit(EXAM_MADE_EASY_MODULE)) return true;
    this.showAccessDenied();
    return false;
  };

  // Navigation gated on the grant. Checked BEFORE history.push, otherwise react-router
  // would have already moved the user into the target page and the check would come
  // too late to stop anything.
  navigateIfEditAllowed = (path) => {
    if (!this.requireEdit()) return;
    this.props.history.push(path);
  };

  /* ---------------- FILE CHANGE ---------------- */

  handleFileChange = (e, type) => {
    const file = e.target.files[0];
    const errorKey = type === "questionPdf" ? "questionError" : "solutionError";
    if (!file) return;

    if (file.type !== "application/pdf") {
      this.setState({ [type]: null, [errorKey]: "Only PDF files allowed" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.setState({ [type]: null, [errorKey]: "File must be less than 5MB" });
      return;
    }

    this.setState({ [type]: file, [errorKey]: "", examError: "", examTypeError: "" });
  };

  /* ---------------- GENERATE FILE NAME ---------------- */

  generateFileName = (file) => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = ("0" + now.getMinutes()).slice(-2);
    const seconds = ("0" + now.getSeconds()).slice(-2);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    hours = ("0" + hours).slice(-2);
    const formatted = `${now.getFullYear()}${("0" + (now.getMonth() + 1)).slice(-2)}${("0" + now.getDate()).slice(-2)}_${hours}${minutes}${seconds}_${ampm}`;
    const extension = file.name.split(".").pop();
    const baseName = file.name.replace(/\.[^/.]+$/, "").replace(/\s+/g, "_");
    return `${baseName}_${formatted}.${extension}`;
  };

  /* ---------------- UPLOAD ---------------- */

  handleUpload = () => {
    // Uploading a PDF creates question data → is_edit. Checked before the field
    // validation so a view-only role is told they lack access, rather than being sent
    // to fix a form they were never allowed to submit. This is also the last gate
    // before the file leaves the client, so it can't be bypassed by a stale render.
    if (!this.requireEdit()) return;

    const { questionPdf, solutionPdf } = this.state;
    const instituteId = parseInt(Cookies.get("institutionid"));
    let questionError = "";
    let solutionError = "";
    let valid = true;

    if (!questionPdf) { questionError = "Please upload Question PDF"; valid = false; }
    // Solution PDF is optional for institute 119, required for everyone else.
    if (instituteId !== 119 && !solutionPdf) { solutionError = "Please upload Solution PDF"; valid = false; }

    if (this.state.selectedExam?.value == undefined) {
      this.setState({ examError: "Please select exam" });
      valid = false;
    }

    if (this.state.selectedExam?.value == 2) {
      if (this.state.selectedExamType?.value == undefined) {
        this.setState({ examTypeError: "Please select exam type" });
        valid = false;
      }
    }

    this.setState({ questionError, solutionError });
    if (!valid) return;

    const formData = new FormData();
    const renamedQuestionPdf = this.generateFileName(questionPdf);

    formData.append("questions_pdf", new File([questionPdf], renamedQuestionPdf, { type: questionPdf.type, lastModified: questionPdf.lastModified }));
    if (solutionPdf) {
      const renamedSolutionPdf = this.generateFileName(solutionPdf);
      formData.append("solutions_pdf", new File([solutionPdf], renamedSolutionPdf, { type: solutionPdf.type, lastModified: solutionPdf.lastModified }));
    }
    formData.append("type", "college");
    formData.append("exam", this.state.selectedExam?.label || "");
    formData.append("institute_id", instituteId);

    let url = "";
    if (instituteId === 119) {
      url = "https://ai.rizee.in/vignan-pdf-ext/vig-autoext";
    } else if (this.state.selectedExamType?.label === "Advance") {
      url = "https://ai.rizee.in/jee-adv-ext/autoext";
    } else {
      url = "https://ai.rizee.in/jee-mains-ext/autoext";
    }

    this.setState({ uploading: true, uploadMessage: "", uploadError: "" });

    fetch(url, { method: "POST", body: formData })
      .then(res => res.json())
      .then((data) => {
        if (data.status === "success") {
          const selectedExamLabel = this.state.selectedExam?.label || "JEE";
          const selectedExamTypeLabel = this.state.selectedExamType?.label || "";
          this.setState({
            uploading: false,
            uploadMessage: data.message || "PDF processed successfully!",
            showCreateExamModal: true,
            questionPdf: null,
            solutionPdf: null,
            activeTab: "list",
            selectedExam: "",
            selectedExamType: ""
          });
          if (this.questionInput) this.questionInput.value = "";
          if (this.solutionInput) this.solutionInput.value = "";
          this.props.getPdfFiles.refetch({
            exams: [selectedExamLabel],
            institute_id: parseInt(Cookies.get("institutionid")),
            pexam_type: selectedExamTypeLabel
          });
        } else {
          this.setState({ uploading: false, uploadError: data.message || "Processing failed." });
        }
      })
      .catch(err => {
        this.setState({ uploading: false, uploadError: "Server error. Please try again." });
        console.error(err);
      });
  };

  /* ---------------- PAGINATION ---------------- */

  handleSearch = (e) => this.setState({ search: e.target.value, page: 1 });
  nextPage = (totalPages) => { if (this.state.page < totalPages) this.setState({ page: this.state.page + 1 }); };
  prevPage = () => { if (this.state.page > 1) this.setState({ page: this.state.page - 1 }); };

  /* ---------------- MODAL ---------------- */

  closePopup = () => this.setState({ showModal: false, questions: [], modalPage: 1, modalTotalQuestions: 0, activeRow: null });
  handleCloseCreateExamModal = () => this.setState({ showCreateExamModal: false, uploadMessage: "" });
  decodeHTML = (text) => decodeURIComponent(text || "");
  renderHTML = (text) => ({ __html: this.decodeHTML(text) });

  /* ---------------- TABLE COLUMNS ---------------- */

  articlecolumns = [
    {
      dataField: "index",
      text: "#",
      formatter: (cell, row, rowIndex) => (
        <span style={{ color: "#374151" }}>
          {rowIndex + 1}
        </span>
      ),
      align: "center",
      headerClasses: "text-center",
    },
    {
      dataField: "file_name",
      text: "PDF Name",
      sort: true,
      formatter: (cell) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <i className="fas fa-file-pdf" style={{ color: "#e63946" }}></i>
          <span style={{ color: "#26336a" }}>{cell}</span>
        </div>
      )
    },
    {
      dataField: "exam_name",
      text: "Exam",
      sort: true,
      formatter: (cell) => (
        <span style={{ color: "#374151" }}>
          {cell || "-"}
        </span>
      )
    },
    {
      dataField: "pexam_type",
      text: "Exam Type",
      sort: true,
      formatter: (cell) => (
        <span style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>
          {cell || "-"}
        </span>
      )
    },
    {
      dataField: "timestamp",
      text: "Inserted Time",
      sort: true,
      formatter: (cell) => (
        <span style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>
          {cell || "-"}
        </span>
      )
    },
    {
      dataField: "Action",
      text: "Action",
      formatter: (cell, row) => (
        <div style={{ display: "flex", gap: "14px", justifyContent: "center", alignItems: "center" }}>
          <i
            className="far fa-eye"
            style={{ color: "#10b981", fontSize: "16px", cursor: "pointer" }}
            onClick={() => this.handleViewPdfQuestions(row)}
            title="View Questions"
          ></i>
          <i
            className="fas fa-edit"
            style={{ color: "#4361ee", fontSize: "16px", cursor: "pointer" }}
            onClick={() => this.navigateIfEditAllowed(`/settings/PDFQuestions/${row?.id}`)}
            title="Edit Questions"
          ></i>
        </div>
      ),
      align: "center",
      headerClasses: "text-center",
    },
    {
      dataField: "CreateExam",
      text: "Create Exam",
      formatter: this.handleCreateExamFun.bind(this),
      align: "center",
      headerClasses: "text-center",
    }
  ];

  handleViewPdfQuestions = async (row, pageNum = 1) => {
    this.setState({ showModal: true, modalLoading: true, questions: [], modalPage: pageNum });
    try {
      const { data } = await this.props.client.query({
        query: gql`
          query AIMLQuestionBankCollegePdf($id: Int!, $institute_id: Int!, $page: Int, $limit: Int) {
            getAIMLFiles: getAIMLQuestionBankCollegePdf(id: $id, institute_id: $institute_id, page: $page, limit: $limit) {
              total_questions
              questions {
                id
                question
                option1
                option2
                option3
                option4
                answer
                explanation
                subject
                chapter
                topic
                compquestion
                class_id
                complexity_1
                exam_1
                qtype
                usageset_1
                question_theory_1
                subject_1
              }
            }
          }
        `,
        variables: {
          id: parseInt(row.id),
          institute_id: parseInt(Cookies.get("institutionid")),
          page: pageNum,
          limit: 25
        },
        fetchPolicy: "network-only"
      });
      const fileData = data?.getAIMLFiles?.[0] || {};
      const questions = fileData.questions || [];
      const totalQuestions = fileData.total_questions || questions.length;
      this.setState({ questions, modalTotalQuestions: totalQuestions, modalLoading: false, activeRow: row });
    } catch (err) {
      console.error(err);
      this.setState({ modalLoading: false });
    }
  };

  handleCreateExamFun(val, row) {
    return (
      <i
        className="fas fa-arrow-right"
        style={{ color: "#4361ee", fontSize: "16px", cursor: "pointer" }}
        onClick={() => this.navigateIfEditAllowed("/questions/create-question-paper/own-question-paper/college-exam")}
        title="Create Exam"
      ></i>
    );
  }

  handleExamChange = (name, value, selectedOption) => {
    this.setState({
      selectedExam: selectedOption,
      selectedExamType: "",
      examError: "",
      examTypeError: ""
    }, () => {
      if (this.state.activeTab === "list") {
        const examName = selectedOption?.label || "JEE";
        this.props.getPdfFiles.refetch({
          exams: [examName],
          institute_id: parseInt(Cookies.get("institutionid")),
          pexam_type: ""
        });
      }
    });
  };

  handleExamTypeChange = (name, value, selectedOption) => {
    this.setState({ selectedExamType: selectedOption, examError: "", examTypeError: "" }, () => {
      if (this.state.activeTab === "list") {
        const examName = this.state.selectedExam?.label || "JEE";
        const examTypeLabel = selectedOption?.label || "";
        this.props.getPdfFiles.refetch({
          exams: [examName],
          institute_id: parseInt(Cookies.get("institutionid")),
          pexam_type: examTypeLabel
        });
      }
    });
  };

  handleTabSelect = (key) => {
    const selectedExamLabel = this.state.selectedExam?.label || "JEE";
    const selectedExamTypeLabel = this.state.selectedExamType?.label || "";
    this.setState({
      activeTab: key,
      selectedExam: "",
      selectedExamType: "",
      examError: "",
      examTypeError: ""
    }, () => {
      if (key === "list") {
        this.props.getPdfFiles.refetch({
          exams: [selectedExamLabel],
          institute_id: parseInt(Cookies.get("institutionid")),
          pexam_type: selectedExamTypeLabel
        });
      }
    });
  };
  /* ---------------- RENDER ---------------- */

  render() {
    const { loading, error, getAIMLFiles } = this.props.getPdfFiles;
    const globals = this.props.globals || {};
    const exams = (globals.globals && globals.globals.exams) || [];
    const classOptions = ((globals.globals && globals.globals.classes) || []).map((c) => ({
      label: c.class,
      value: c.id,
    }));

    if (error) {
      alert("Server Error " + error.message);
      return null;
    }

    const pdfList = getAIMLFiles || [];
    const { page, limit, search, modalPage, modalLimit, questions, modalTotalQuestions, activeRow } = this.state;

    const filteredList = pdfList.filter((pdf) =>
      pdf.file_name.toLowerCase().includes(search.toLowerCase())
    );

    const totalPages = Math.ceil(filteredList.length / limit);

    const totalQuestions = modalTotalQuestions || questions.length;
    const totalModalPages = Math.ceil(totalQuestions / modalLimit);
    const modalStartIndex = (modalPage - 1) * modalLimit;
    const paginatedQuestions = questions; // Paginated by backend


    return (
      <div>

        <Card>
          <Card.Body>
            <div>

              {/* ---- Main Card ---- */}

              <Row className="align-items-center g-2 border-bottom pb-2 mb-2">
                <Col xs={12} md={9}>
                  <Tabs
                    activeKey={this.state.activeTab}
                    onSelect={this.handleTabSelect}
                    className="custom-attendance-tabs border-bottom-0 mb-0"
                  >
                    {FILTERS.map((filter) => (
                      <Tab
                        eventKey={filter.value}
                        title={
                          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                            <i className={filter.icon} style={{ fontSize: "16px", color: this.state.activeTab === filter.value ? "#6366f1" : "#4b5563" }}></i>
                            <span style={{ color: this.state.activeTab === filter.value ? "#6366f1" : "#4b5563" }}>{filter.label}</span>
                          </div>
                        }
                        key={filter.value}
                      />
                    ))}
                  </Tabs>
                </Col>
              </Row>

              {/* ======== PDF LIST TAB CONTENT ======== */}
              {this.state.activeTab === "list" && (
                <div>
                  {loading ? (
                    <div className="pdf-table-wrapper">
                      <style>{`
                    @keyframes pdfShimmer {
                      0%   { background-position: -468px 0; }
                      100% { background-position: 468px 0; }
                    }
                    .pdf-shimmer {
                      background: #f1f5f9;
                      background-image: linear-gradient(90deg, #eef2f7 0px, #f8fafc 40px, #eef2f7 80px);
                      background-size: 936px 100%;
                      background-repeat: no-repeat;
                      border-radius: 8px;
                      animation: pdfShimmer 1.4s linear infinite;
                    }
                  `}</style>

                      {/* Toolbar shimmer */}
                      <div className="pdf-table-toolbar">
                        <div className="pdf-shimmer" style={{ width: 280, height: 40 }} />
                        <div className="pdf-shimmer" style={{ width: 90, height: 28, borderRadius: 20 }} />
                      </div>

                      {/* Header shimmer */}
                      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 16px", marginTop: 8 }}>
                        <div className="pdf-shimmer" style={{ width: 30, height: 14 }} />
                        <div className="pdf-shimmer" style={{ flex: 1, height: 14 }} />
                        <div className="pdf-shimmer" style={{ width: 60, height: 14 }} />
                      </div>

                      {/* Row shimmers */}
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px" }}>
                          <div className="pdf-shimmer" style={{ width: 28, height: 28, borderRadius: "50%" }} />
                          <div className="pdf-shimmer" style={{ width: 20, height: 22 }} />
                          <div className="pdf-shimmer" style={{ flex: 1, height: 14, maxWidth: 360 }} />
                          <div className="pdf-shimmer" style={{ width: 36, height: 30, marginLeft: "auto" }} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="pdf-table-wrapper">

                      <Row>
                        {/* Exam Type Dropdown */}
                        <Col lg={4} md={6} sm={6} xs={12} className="mb-4">
                          <label className="control-label">Select Exam</label>
                          <SelectDropDown
                            name="exam"
                            isClearable={false}
                            options={exams.slice(0, 2).map((exam) => {
                              return {
                                label: exam.exam,
                                value: exam.id
                              };
                            })}
                            handleChange={this.handleExamChange}
                            stateData={this.state.selectedExam}
                          />
                          {
                            this.state.examError && (
                              <span className="text-danger">{this.state.examError}</span>
                            )
                          }
                        </Col>

                        {/* Exam Dropdown */}
                        {
                          (this.state.selectedExam?.label?.toUpperCase().includes("JEE") || this.state.selectedExam?.value === 2 || String(this.state.selectedExam?.value) === "2") && (
                            <Col lg={4} md={6} sm={6} xs={12} className="mb-4">
                              <label className="control-label">Select Exam Type</label>
                              <SelectDropDown
                                name="exam"
                                isClearable={false}
                                options={[{ value: 1, label: 'Mains' }, { value: 2, label: 'Advance' }]}
                                handleChange={this.handleExamTypeChange}
                                stateData={this.state.selectedExamType}
                              />
                              {
                                this.state.examTypeError && (
                                  <span className="text-danger">{this.state.examTypeError}</span>
                                )
                              }
                            </Col>
                          )
                        }
                      </Row>

                      {/* Toolbar */}
                      <div className="pdf-table-toolbar">
                        <div className="search-box">
                          <i className="fas fa-search"></i>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search PDF by name..."
                            value={this.state.search}
                            onChange={this.handleSearch}
                          />
                        </div>
                        <span className="pdf-count-badge">
                          <i className="fas fa-file-pdf me-1"></i>
                          {filteredList.length} PDFs
                        </span>
                      </div>

                      {/* Table */}
                      <Container fluid style={{ padding: 0 }}>
                        <CardLessDataTableWithOutSearch
                          parentData={filteredList}
                          particlecolumns={this.articlecolumns}
                          name="pdf_list"
                        />
                      </Container>

                    </div>
                  )}
                </div>
              )}

              {/* ======== PDF UPLOAD TAB CONTENT ======== */}
              {this.state.activeTab === "upload" && (
                <div className="upload-container">

                  <div className="upload-section-title">
                    <i className="fas fa-upload"></i>
                    Upload New PDFs
                  </div>

                  <Row>
                    {/* Exam Type Dropdown */}
                    <Col lg={4} md={6} sm={6} xs={12} className="mb-4">
                      <label className="control-label">Select Exam</label>
                      <SelectDropDown
                        name="exam"
                        isClearable={false}
                        options={exams.slice(0, 2).map((exam) => {
                          return {
                            label: exam.exam,
                            value: exam.id
                          };
                        })}
                        handleChange={this.handleExamChange}
                        stateData={this.state.selectedExam}
                      />
                      {
                        this.state.examError && (
                          <span className="text-danger">{this.state.examError}</span>
                        )
                      }
                    </Col>

                    {/* Exam Dropdown */}
                    {
                      (this.state.selectedExam?.label?.toUpperCase().includes("JEE") || this.state.selectedExam?.value === 2 || String(this.state.selectedExam?.value) === "2") && (
                        <Col lg={4} md={6} sm={6} xs={12} className="mb-4">
                          <label className="control-label">Select Exam Type</label>
                          <SelectDropDown
                            name="exam"
                            isClearable={false}
                            options={[{ value: 1, label: 'Mains' }, { value: 2, label: 'Advance' }]}
                            handleChange={this.handleExamTypeChange}
                            stateData={this.state.selectedExamType}
                          />
                          {
                            this.state.examTypeError && (
                              <span className="text-danger">{this.state.examTypeError}</span>
                            )
                          }
                        </Col>
                      )
                    }
                  </Row>

                  <div className="row">
                    {/* Question PDF */}
                    <div className="col-md-6">
                      <div
                        className={`upload-card ${this.state.questionPdf ? "has-file" : ""}`}
                        onClick={() => this.questionInput && this.questionInput.click()}
                      >
                        <i className="fas fa-file-pdf upload-icon"></i>
                        <div className="upload-title">Question PDF</div>
                        <div className="upload-subtitle">Max size: 5MB · PDF only</div>
                        <Form.Control
                          type="file"
                          accept="application/pdf"
                          ref={(ref) => (this.questionInput = ref)}
                          onChange={(e) => this.handleFileChange(e, "questionPdf")}
                          style={{ display: "none" }}
                        />
                        {this.state.questionPdf && (
                          <div className="upload-file-name">
                            <i className="fas fa-check-circle"></i>
                            {this.state.questionPdf.name}
                          </div>
                        )}
                        {this.state.questionError && (
                          <div className="upload-error">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {this.state.questionError}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Solution PDF */}
                    {
                      instituteId === 119 ? ("") : (
                        <div className="col-md-6">
                          <div
                            className={`upload-card ${this.state.solutionPdf ? "has-file" : ""}`}
                            onClick={() => this.solutionInput && this.solutionInput.click()}
                          >
                            <i className="fas fa-book upload-icon solution"></i>
                            <div className="upload-title">Solution PDF</div>
                            <div className="upload-subtitle">Max size: 5MB · PDF only</div>
                            <Form.Control
                              type="file"
                              accept="application/pdf"
                              ref={(ref) => (this.solutionInput = ref)}
                              onChange={(e) => this.handleFileChange(e, "solutionPdf")}
                              style={{ display: "none" }}
                            />
                            {this.state.solutionPdf && (
                              <div className="upload-file-name">
                                <i className="fas fa-check-circle"></i>
                                {this.state.solutionPdf.name}
                              </div>
                            )}
                            {this.state.solutionError && (
                              <div className="upload-error">
                                <i className="fas fa-exclamation-circle me-1"></i>
                                {this.state.solutionError}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    }

                  </div>

                  {/* Status messages */}
                  {this.state.uploading && (
                    <div className="upload-status-box loading">
                      <div className="spinner-border spinner-border-sm" style={{ color: "#4361ee" }}></div>
                      <span>Processing your PDFs, please wait...</span>
                    </div>
                  )}

                  {this.state.uploadMessage && (
                    <div className="upload-status-box success">
                      <i className="fas fa-check-circle" style={{ fontSize: "18px", color: "#06d6a0" }}></i>
                      <span>{this.state.uploadMessage}</span>
                    </div>
                  )}

                  {this.state.uploadError && (
                    <div className="upload-status-box error">
                      <i className="fas fa-times-circle" style={{ fontSize: "18px", color: "#e63946" }}></i>
                      <span>{this.state.uploadError}</span>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="upload-btn-wrapper">
                    <Button
                      className="upload-main-btn"
                      onClick={this.handleUpload}
                      disabled={this.state.uploading}
                    >
                      {this.state.uploading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cloud-upload-alt me-2"></i>
                          Upload &amp; Process PDFs
                        </>
                      )}
                    </Button>
                  </div>

                </div>
              )}

              {/* ======== ANSWER & EXPLANATION TAB CONTENT ======== */}
              {this.state.activeTab === "answer-explanation" && (
                <ExamAnswerExplanationUpload onUploaded={() => this.handleTabSelect("list")} />
              )}

            </div>
          </Card.Body>
        </Card>

        {/* ======== QUESTIONS MODAL ======== */}
        <Modal
          show={this.state.showModal}
          onHide={this.closePopup}
          size="lg"
          dialogClassName="pdf-analysis-modal"
        >
          <Modal.Header closeButton className="pdf-modal-header">
            <Modal.Title>
              <i className="fas fa-question-circle me-2"></i>
              Questions
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto", padding: "20px" }}>
            {this.state.modalLoading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spinner animation="border" variant="primary" />
                <div style={{ marginTop: "12px", color: "#6b7280" }}>Loading questions...</div>
              </div>
            ) : paginatedQuestions.length > 0 ? (
              <MathJaxContext version={3} config={mathJaxConfig}>
                <MathJax dynamic>
                  {paginatedQuestions.map((q, index) => (
                    <div
                      key={q.id}
                      className="question-card"
                      style={{ border: "1px solid #edf0ff", padding: "16px", marginBottom: "16px", borderRadius: "12px" }}
                    >
                      <div className="question-label">Q No. {modalStartIndex + index + 1}</div>
                      <div>Q.Id {q?.id}</div>
                      <br />
                      <div className="meta-inline">
                        <span><b>Exam:</b> {q.exam_1 || "-"}</span>
                        <span><b>Class:</b> {classOptions.filter((item) => +item.value === +q.class_id)[0]?.label || "-"}</span>
                        <span><b>Subject:</b> {q.subject_1 || "-"}</span>
                        <span><b>Chapter:</b> {q.chapter || "-"}</span>
                        <span><b>Topic:</b> {q.topic || "-"}</span>
                        <span><b>Type:</b> {q.qtype || "-"}</span>
                        <span><b>Complexity:</b> {q.complexity_1 || "-"}</span>
                        <span><b>Theory:</b> {q.question_theory_1 || "-"}</span>
                        {/* <span><b>Usage:</b> {q.usageset_1 || "-"}</span> */}
                      </div>

                      <div className="question-box">
                        {q.compquestion != 0 && q.compquestion != null && q.compquestion != "" && (
                          <div className="comprehension-box mb-3" style={{ background: "#f8fafc", padding: "12px", borderRadius: "4px" }}>
                            <MathRenderer text={q.compquestion} uniqueKey={`modal-comp-${q.id}`} className="question-text" />
                          </div>
                        )}
                        <MathRenderer text={q.question} uniqueKey={`modal-q-${q.id}`} className="question-text" />
                      </div>

                      <div className="options-grid">
                        {[q.option1, q.option2, q.option3, q.option4].map((opt, i) => {
                          const label = ["A", "B", "C", "D"][i];
                          const cleanAnswers = String(q.answer || "")
                            .split(/[\s,]+/)
                            .map(ans => ans.replace(/[^A-Za-z0-9]/g, "").toUpperCase().trim())
                            .filter(Boolean);
                          const isAnswer = cleanAnswers.includes(label) || cleanAnswers.includes(String(i + 1));
                          return opt ? (
                            <div className={`option-card ${isAnswer ? "correct" : ""}`} key={i}>
                              <div className="option-label">{label}</div>
                              <MathRenderer text={opt} uniqueKey={`modal-opt-${q.id}-${i}`} />
                            </div>
                          ) : null;
                        })}
                      </div>

                      <div className="answer-box">
                        <strong>Answer:</strong> {q.answer}
                      </div>

                      {q.explanation && (
                        <div className="explanation-box">
                          <div className="explanation-title">Explanation</div>
                          <MathRenderer text={q.explanation} uniqueKey={`modal-exp-${q.id}`} />
                        </div>
                      )}
                    </div>
                  ))}
                  {totalModalPages > 1 && (
                    <div className="pdf-pagination mb-4 mt-4">
                      <Button
                        className="btn-page"
                        onClick={() => {
                          this.handleViewPdfQuestions(activeRow, modalPage - 1);
                          const modalBody = document.querySelector(".pdf-analysis-modal .modal-body");
                          if (modalBody) modalBody.scrollTop = 0;
                        }}
                        disabled={modalPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="page-info">
                        Page <b>{modalPage}</b> of <b>{totalModalPages}</b> (Showing {modalStartIndex + 1}-{Math.min(modalStartIndex + modalLimit, totalQuestions)} of {totalQuestions})
                      </span>
                      <Button
                        className="btn-page"
                        onClick={() => {
                          this.handleViewPdfQuestions(activeRow, modalPage + 1);
                          const modalBody = document.querySelector(".pdf-analysis-modal .modal-body");
                          if (modalBody) modalBody.scrollTop = 0;
                        }}
                        disabled={modalPage === totalModalPages}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </MathJax>
              </MathJaxContext>
            ) : (
              <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
                <i className="fas fa-inbox" style={{ fontSize: "40px", marginBottom: "12px", display: "block" }}></i>
                No Questions Available
              </div>
            )}
          </Modal.Body>

          <Modal.Footer style={{ borderTop: "1px solid #edf0ff" }}>
            <Button
              style={{
                background: "linear-gradient(135deg, #26336a, #4361ee)",
                border: "none",
                borderRadius: "20px",
                padding: "8px 24px",
                fontWeight: 600
              }}
              onClick={this.closePopup}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ======== CREATE EXAM PROMPT MODAL ======== */}
        <Modal
          show={this.state.showCreateExamModal}
          onHide={this.handleCloseCreateExamModal}
          centered
        >
          <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #26336a, #4361ee)", color: "#fff" }}>
            <Modal.Title>PDF Processed Successfully!</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <i className="fas fa-check-circle text-success mb-3" style={{ fontSize: "50px" }}></i>
            <h4>Would you like to create an exam now?</h4>
            <p className="text-muted">Your PDF has been processed. You can now proceed to create a college exam with these questions.</p>
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button
              variant="secondary"
              onClick={this.handleCloseCreateExamModal}
              style={{ borderRadius: "20px", padding: "8px 24px" }}
            >
              Back to List
            </Button>
            <Button
              style={{
                background: "linear-gradient(135deg, #26336a, #4361ee)",
                border: "none",
                borderRadius: "20px",
                padding: "8px 24px",
                fontWeight: 600
              }}
              onClick={() => this.navigateIfEditAllowed("/questions/create-question-paper/own-question-paper/college-exam")}
            >
              Create Exam
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ======== ACCESS DENIED MODAL ======== */}
        {/* Shown instead of uploading / navigating when the grant is missing. One
            modal serves every intercepted action on this tab. */}
        <Modal
          show={this.state.accessDeniedShow}
          onHide={this.hideAccessDenied}
          centered
          size="sm"
          aria-labelledby="pdf-questions-access-denied"
        >
          <Modal.Body className="text-center" style={{ padding: "28px 24px" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#fef2f2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <i className="fal fa-lock-alt" style={{ fontSize: 26 }} />
            </div>
            <h5 id="pdf-questions-access-denied" style={{ fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
              Access Denied
            </h5>
            <p style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>
              You don't have permission to perform this action.
            </p>
            <Button variant="secondary" onClick={this.hideAccessDenied}>Close</Button>
          </Modal.Body>
        </Modal>

      </div>
    );
  }
}

export default compose(
  withRouter,
  withApollo,
  graphql(FETCH_PDF_FILES_DATA, {
    options: () => ({
      variables: {
        exams: ["JEE"],
        institute_id: parseInt(Cookies.get("institutionid")),
        pexam_type: ""
      }
    }),
    name: "getPdfFiles"
  }),
  graphql(FETCH_GLOBALS, {
    options: () => ({
      variables: {
        institution_id: parseInt(Cookies.get("institutionid"))
      }
    }),
    name: "globals"
  })
)(PdfTabs);
